import { useState } from 'react'
import { Facebook, Twitter, Linkedin, Instagram, FileVideo2 } from 'lucide-react'
import { useParams } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Pagination from '@/components/ui/Pagination'
import { useFilterVideos, useSearchVideos } from '@/lib/api/hooks/video'
import type { Video } from '@/lib/api/types'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { parseHtmlString } from '@/utils/helpers/html'
import { getVideoPreviewImage } from '@afsa/shared/utils'
import RecentArticles from '@/components/sections/RecentArticles'

import Search from '@/components/ui/Search'
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams'
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay'

import FILTER_SECTIONS from '@/components/pages/Media/Videos/FilterModule/FILTER_SECTIONS'

import FilterModule from '@/components/ui/FilterModule'


// Helper function to extract YouTube video ID from URL
const extractYouTubeId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }

  return null
}

export default function Videos() {
  const { folderId } = useParams<{ folderId: string }>()
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null)

  // ── URL-driven search / filter state ──────────────────────────────────────
  const {
    mode,
    searchTerm,
    filterState,
    page,
    size,
    setSearch,
    setFilters,
    removeFilter,
    clearFilters,
    setPage,
    apiFilters,
    getActiveFilterCount
  } = useSearchFilterParams(FILTER_SECTIONS)

  // Fetch videos with filters
  const { data: videosResponse, isLoading: videosLoading, isError: videosError, refetch: refetchVideos } = useFilterVideos(
    { page, size },
    apiFilters
  )

    // ── Search query  ─────────────────────
    const { data: searchData, isLoading: isSearchLoading, error: searchError } = useSearchVideos(
      { searchTerm, folderId: folderId ?? '' },
      { page, size }
    )

    const videoData: Video[] = mode === 'search' ? searchData?.data?.data || [] : videosResponse?.data?.data || []
    const videos = Array.isArray(videoData) && videoData.length > 0 ? videoData.map(video => ({
      ...video
    })) : []
      const error = mode === 'search' ? searchError : videosError
      const refetchData = mode === 'search' ? refetchVideos : refetchVideos
      const loading = mode === 'search' ? isSearchLoading : videosLoading
      const totalPages = mode === 'search' ? searchData?.data?.totalPage || 1 : videosResponse?.data?.totalPage || 1
      const activeFilterCount = getActiveFilterCount(FILTER_SECTIONS)
      const emptyMessage = mode === 'search' ? 'No videos found for your search.' : mode === "filter" ? 'No videos found for the selected filters.' : 'No videos has been added'


  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Videos', href: '/media/videos' },
    { label: 'Video Gallery', href: '', active: true }
  ]

  // Handle video card click
  const handleVideoClick = (video: Video) => {
    setSelectedVideo(video)
  }

  // Handle closing video modal
  const handleCloseVideo = () => {
    setSelectedVideo(null)
  }

                           const getPreviewImage = (thumbnailUrl: string | undefined | null) => {
        if (thumbnailUrl) {
            const image = getVideoPreviewImage(thumbnailUrl)
            return image ? (
                <img
                    src={image}
                    alt="Video Preview"
                    className="w-full h-full object-cover group-hover:opacity-75 transition-opacity"
                    onError={(e) => {
                        e.currentTarget.src = 'https://placehold.co/600x400/E5E7EB/6B7280?text=No+Image'
                    }}
                />
            ) : <FileVideo2 className="w-24 h-24 text-gray-300" />
         }
            return <FileVideo2 className="w-24 h-24 text-gray-300" />

        }


  return (
    <motion.div
      className="bg-white min-h-screen"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-5" variants={fadeUp}>
        <Breadcrumb items={breadcrumbItems} />
      </motion.div>

      {/* Main Content */}
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16" variants={fadeUp}>
        <motion.div
          className="flex flex-col lg:flex-row gap-12"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportReveal}
        >
          {/* Sidebar */}
          <div className="w-full lg:w-96 flex flex-col gap-12">
           
              {/* Recent Articles */}
              <RecentArticles />

            {/* Social Links */}
            <div className="flex flex-col gap-4">
              <h3 className="text-lg text-gray-500 font-['PT_Serif']">OUR SOCIALS</h3>
              <div className="flex gap-2">
                <button className="w-6 h-6 hover:opacity-70">
                  <Facebook className="w-5 h-5 text-gray-800" />
                </button>
                <button className="w-6 h-6 hover:opacity-70">
                  <Twitter className="w-5 h-5 text-gray-800" />
                </button>
                <button className="w-6 h-6 hover:opacity-70">
                  <Instagram className="w-6 h-6 text-gray-800" />
                </button>
                <button className="w-6 h-6 hover:opacity-70">
                  <Linkedin className="w-6 h-6 text-gray-800" />
                </button>
              </div>
            </div>
          </div>

          {/* Video Gallery Grid */}
          <div className="flex-1 space-y-8">
          {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
             
             {/* It will be shown when the endpoint is ready */}
             <div style={{display: "none"}}>
                 <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search events..."
              />
             </div>
           
               <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>


            <div className="flex flex-col gap-8">
              {loading ? (
                <div className="text-center py-12">
                  <p className="text-lg text-gray-500">Fetching results...</p>
                </div>
              ) :
              error ? (
                <div className="text-center py-12">
                  <p className="text-lg text-gray-500">Error loading videos.</p>
                  <button onClick={() => refetchData()} className="px-4 py-2 bg-wine-100 hover:bg-wine-200 text-white rounded">Retry</button>
                </div>
              ) : videos.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-lg text-gray-500">{emptyMessage}</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {videos.map((video) => {
                      // Use custom thumbnail if available, otherwise use placeholder

                      return (
                        <div
                          key={video.videoUploadId}
                          onClick={() => handleVideoClick(video)}
                          className="shadow-lg flex flex-col hover:shadow-xl transition-shadow cursor-pointer group"
                        >
                          <div className="relative w-full h-40 bg-gray-900 overflow-hidden">
                      
                            {getPreviewImage(video.thumbnailUrl)}
                            {/* Play button overlay */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w12 h12 bg-wine-100 rounded-md flex items-center justify-center group-hover:bg-wine-200 transition-colors">
                                <svg
                                  className="w-8 h-8 text-white ml-1"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </div>
                            </div>
                          </div>
                          <div className="p-4 bg-white flex flex-col gap-2">
                            <h3 className="text-lg font-medium text-gray-900 line-clamp-2">
                              {video.title}
                            </h3>
                            <p className="text-sm text-gray-600 line-clamp-2">
                              <span dangerouslySetInnerHTML={parseHtmlString(video.description)} />
                            </p>
                            <div className="text-xs text-gray-500 mt-2">
                              {new Date(video.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

             
                    <div className="">
                      <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                      />
                    </div>
                
                </>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={handleCloseVideo}
        >
          <div
            className="relative w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={handleCloseVideo}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Video player */}
            <div className="bg-black rounded-lg overflow-hidden">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${extractYouTubeId(selectedVideo.embeddedLink)}`}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>

              {/* Video details */}
              <div className="p-6 bg-white">
                <h2 className="text-2xl font-medium text-gray-900 mb-2">
                  {selectedVideo.title}
                </h2>
                <p className="text-gray-600 mb-4">
                  <span dangerouslySetInnerHTML={parseHtmlString(selectedVideo.description)} />
                </p>
                <div className="text-sm text-gray-500">
                  Published: {new Date(selectedVideo.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}

